import axios from 'axios';

// In production on Railway, use the backend service URL
// VITE_API_URL should be set as an environment variable in Railway
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

console.log('API URL:', API_URL); // Debug log to verify in production

// Create axios instance
export const apiClient = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // For cookies
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/api/auth/refresh`, {
            refreshToken
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data;
          
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ========================================
// ENTITY CLIENT (replaces base44.entities)
// ========================================

class EntityClient {
  constructor(entityName) {
    this.entityName = entityName;
    this.basePath = `/entities/${entityName}`;
  }

  async list(orderBy = '-created_at') {
    const response = await apiClient.get(`${this.basePath}/list`, {
      params: { orderBy }
    });
    return response.data;
  }

  async filter(filters) {
    const response = await apiClient.post(`${this.basePath}/filter`, filters);
    return response.data;
  }

  async create(data) {
    const response = await apiClient.post(`${this.basePath}/create`, data);
    return response.data;
  }

  async update(id, data) {
    const response = await apiClient.patch(`${this.basePath}/update/${id}`, data);
    return response.data;
  }

  async delete(id) {
    const response = await apiClient.delete(`${this.basePath}/delete/${id}`);
    return response.data;
  }

  async bulkCreate(items) {
    const response = await apiClient.post(`${this.basePath}/bulkCreate`, items);
    return response.data;
  }
}

// ========================================
// AUTH CLIENT (replaces base44.auth)
// ========================================

export const auth = {
  async register(email, password, full_name, user_role = 'employee') {
    const response = await apiClient.post('/auth/register', {
      email,
      password,
      full_name,
      user_role
    });

    const { accessToken, refreshToken, user } = response.data;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);

    return user;
  },

  async login(email, password, twoFactorCode) {
    const response = await apiClient.post('/auth/login', {
      email,
      password,
      twoFactorCode
    });

    // Check if 2FA is required
    if (response.data.requires2FA) {
      return response.data; // Return the 2FA requirement info
    }

    const { accessToken, refreshToken, user } = response.data;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);

    return user;
  },

  async logout() {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  },

  async me() {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  async updateMe(data) {
    const response = await apiClient.patch('/auth/me', data);
    return response.data;
  },

  async refresh(refreshToken) {
    const response = await apiClient.post('/auth/refresh', { refreshToken });
    const { accessToken, refreshToken: newRefreshToken } = response.data;
    
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', newRefreshToken);

    return { accessToken, refreshToken: newRefreshToken };
  },

  async isAuthenticated() {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        return false;
      }

      // Try to get current user to verify token is valid
      await this.me();
      return true;
    } catch (error) {
      // If token is invalid or expired, clear it
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      return false;
    }
  },

  redirectToLogin(returnUrl) {
    // Redirect to the login page
    // If a return URL is provided, store it and redirect after login
    if (returnUrl) {
      localStorage.setItem('returnUrl', returnUrl);
    }
    window.location.href = '/Login';
  },

  redirectToSignup() {
    // Redirect to signup page
    window.location.href = '/company-signup';
  },

  getReturnUrl() {
    const returnUrl = localStorage.getItem('returnUrl');
    localStorage.removeItem('returnUrl');
    return returnUrl || '/';
  }
};

// ========================================
// ENTITIES (replaces base44.entities)
// ========================================

const proposalEntity = new EntityClient('Proposal');
proposalEntity.delete = async (id) => {
  const response = await apiClient.delete(`/entities/Proposal/delete/${id}`);
  return response.data;
};

const proposalCensusRowEntity = new EntityClient('ProposalCensusRow');
proposalCensusRowEntity.deleteByProposal = async (proposalId) => {
  const response = await apiClient.delete(`/entities/ProposalCensusRow/deleteByProposal/${proposalId}`);
  return response.data;
};

export const entities = {
  Employee: new EntityClient('Employee'),
  Employer: new EntityClient('Employer'),
  Enrollment: new EntityClient('Enrollment'),
  BenefitProduct: new EntityClient('BenefitProduct'),
  BenefitCategory: new EntityClient('BenefitCategory'),
  FamilyMember: new EntityClient('FamilyMember'),
  SystemConfig: new EntityClient('SystemConfig'),
  Notification: new EntityClient('Notification'),
  AuditLog: new EntityClient('AuditLog'),
  TaxRateConfig: new EntityClient('TaxRateConfig'),
  FicaConfig: new EntityClient('FicaConfig'),
  RatingTable: new EntityClient('RatingTable'),
  Claim: new EntityClient('Claim'),
  QualifyingLifeEvent: new EntityClient('QualifyingLifeEvent'),
  EnrollmentPeriod: new EntityClient('EnrollmentPeriod'),
  EmailTemplate: new EntityClient('EmailTemplate'),
  User: new EntityClient('User'),
  Proposal: proposalEntity,
  ProposalCensusRow: proposalCensusRowEntity
};

// ========================================
// EMAIL CLIENT
// ========================================

export const email = {
  async send(to, subject, html, text) {
    const response = await apiClient.post('/email/send', {
      to,
      subject,
      html,
      text
    });
    return response.data;
  }
};

// ========================================
// FUNCTIONS (for backwards compatibility)
// ========================================

export const functions = {
  async invoke(module, functionName, payload) {
    // Handle migrations functions
    if (module === 'migrations') {
      if (functionName === 'status') {
        const response = await apiClient.get(`/migrations/${functionName}`);
        return response.data;
      } else if (functionName === 'deploy') {
        const response = await apiClient.post(`/migrations/${functionName}`);
        return response.data;
      } else {
        const response = await apiClient.post(`/migrations/${functionName}`, payload);
        return response.data;
      }
    }
    
    // Map legacy function calls to new API endpoints
    if (module === 'sendSMTPEmail') {
      return await email.send(payload.to, payload.subject, payload.html, payload.text);
    }
    throw new Error(`Function ${module}/${functionName} not found`);
  }
};

// ========================================
// APP LOGS CLIENT
// ========================================

export const appLogs = {
  async logUserInApp(pageName, actionType = 'page_view', metadata = null) {
    try {
      const response = await apiClient.post('/app-logs/log', {
        page_name: pageName,
        action_type: actionType,
        metadata
      });
      return response.data;
    } catch (error) {
      // Silently fail - logging shouldn't break the app
      console.warn('Failed to log user activity:', error);
      return null;
    }
  },

  async getMyActivity(limit = 50, offset = 0) {
    const response = await apiClient.get('/app-logs/my-activity', {
      params: { limit, offset }
    });
    return response.data;
  },

  async list(filters = {}) {
    const response = await apiClient.get('/app-logs/list', {
      params: filters
    });
    return response.data;
  },

  async getAnalytics(startDate, endDate) {
    const response = await apiClient.get('/app-logs/analytics', {
      params: { start_date: startDate, end_date: endDate }
    });
    return response.data;
  }
};

// ========================================
// AGENTS CLIENT (for AI conversations)
// ========================================

// In-memory storage for conversation subscriptions
const conversationSubscriptions = new Map();

export const agents = {
  async createConversation(data) {
    try {
      // For now, create a mock conversation in memory
      // In production, this would call a backend API
      const conversationId = `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const conversation = {
        id: conversationId,
        ...data,
        messages: [],
        created_at: new Date().toISOString()
      };
      
      // Store in sessionStorage for persistence across reloads
      sessionStorage.setItem(`conversation_${conversationId}`, JSON.stringify(conversation));
      
      return conversation;
    } catch (error) {
      console.error('Failed to create conversation:', error);
      throw error;
    }
  },

  async getConversation(conversationId) {
    try {
      // Retrieve from sessionStorage
      const stored = sessionStorage.getItem(`conversation_${conversationId}`);
      if (stored) {
        return JSON.parse(stored);
      }
      throw new Error('Conversation not found');
    } catch (error) {
      console.error('Failed to get conversation:', error);
      throw error;
    }
  },

  async addMessage(conversation, message) {
    try {
      const conversationId = typeof conversation === 'string' ? conversation : conversation.id;
      const conv = await this.getConversation(conversationId);
      
      // Add message to conversation
      conv.messages = conv.messages || [];
      conv.messages.push({
        ...message,
        timestamp: new Date().toISOString()
      });
      
      // Update storage
      sessionStorage.setItem(`conversation_${conversationId}`, JSON.stringify(conv));
      
      // Notify subscribers
      const subscribers = conversationSubscriptions.get(conversationId);
      if (subscribers) {
        subscribers.forEach(callback => callback(conv));
      }
      
      return conv;
    } catch (error) {
      console.error('Failed to add message:', error);
      throw error;
    }
  },

  subscribeToConversation(conversationId, callback) {
    // Add subscriber
    if (!conversationSubscriptions.has(conversationId)) {
      conversationSubscriptions.set(conversationId, new Set());
    }
    conversationSubscriptions.get(conversationId).add(callback);
    
    // Return unsubscribe function
    return () => {
      const subscribers = conversationSubscriptions.get(conversationId);
      if (subscribers) {
        subscribers.delete(callback);
        if (subscribers.size === 0) {
          conversationSubscriptions.delete(conversationId);
        }
      }
    };
  },

  async sendInbandMessage(conversationId, contextUpdate) {
    try {
      // Send a system message with context update
      const systemMessage = {
        role: 'system',
        content: `[Context Update] ${contextUpdate.message}`,
        metadata: contextUpdate,
        timestamp: new Date().toISOString()
      };
      
      await this.addMessage(conversationId, systemMessage);
      
      return systemMessage;
    } catch (error) {
      console.error('Failed to send inband message:', error);
      throw error;
    }
  },

  clearConversation(conversationId) {
    sessionStorage.removeItem(`conversation_${conversationId}`);
    conversationSubscriptions.delete(conversationId);
  }
};

// Import integrations
import * as integrations from './integrations.js';

// ========================================
// MAIN API CLIENT (replaces base44)
// ========================================

export const api = {
  auth,
  entities,
  email,
  functions,
  appLogs,
  agents,
  integrations,
  
  // For backwards compatibility, you can still use asServiceRole
  // but it's just an alias to entities now (permissions handled server-side)
  asServiceRole: {
    entities
  }
};

// Debug logging
console.log('API Client loaded:', {
  hasAuth: !!api.auth,
  hasIsAuthenticated: typeof api.auth?.isAuthenticated,
  hasRedirectToLogin: typeof api.auth?.redirectToLogin,
  authKeys: api.auth ? Object.keys(api.auth) : []
});

export default api;
